import type { ModelParameterSupport } from "../model-parameters.ts";
import type { CatalogueModel, InferenceGeography, ModelRole } from "../models.ts";
import { CANONICAL_MODELS } from "./canonical.generated.ts";
import type { CanonicalModel } from "./canonical.ts";
import {
  BEDROCK_OFFERINGS,
  type BedrockOffering,
  type OfferingKey,
  type OfferingPrice,
  type PriceRoute,
} from "./offerings.ts";
import { defaultDerivationScope, deriveCandidates } from "./versions.ts";

/**
 * Today's catalogue exports as views of the two layers (ADR 0017 §1): same names, shapes, key
 * order and values as the hand-written tables they replace (model-catalogue D1). Only type
 * imports from `../models.ts`, which re-exports these: no runtime cycle.
 */

/** `INFERENCE_GEOGRAPHIES` order (a runtime import would be circular); a test pins it. */
export const VIEW_GEOGRAPHIES = ["eu", "global"] as const satisfies readonly InferenceGeography[];

/** An offering as a catalogue entry (`consentRequired` derived from its implications). */
export function catalogueModel(offering: BedrockOffering): CatalogueModel {
  const { retention, providerReceivesData, relativeCost, relativeSpeed } = offering.implications;
  return {
    displayName: offering.displayName,
    provider: offering.provider,
    implications: {
      retention,
      geographies: VIEW_GEOGRAPHIES.filter((g) => offering.routes.profiles[g] !== undefined),
      providerReceivesData,
      relativeCost,
      relativeSpeed,
    },
    implicationsVersion: offering.implicationsVersion,
    consentRequired: retention !== "default" || providerReceivesData,
    reasoning: offering.reasoning !== null,
    parameters: parameterSupport(offering),
    ...(offering.retired && { retired: true }),
  };
}

/**
 * The parameters an offering supports (model-picking D15): its curated reasoning style, and the
 * context window and maximum output of its canonical model (the drift test requires both).
 */
export function parameterSupport(offering: BedrockOffering): ModelParameterSupport {
  const limit = CANONICAL_MODELS[offering.canonical]?.limit;
  return {
    reasoning: offering.reasoning,
    contextWindow: limit?.context ?? 0,
    maxOutput: limit?.output ?? 0,
  };
}

/** Every model ESI can call, keyed by foundation model (`foundationModelOf` of a profile ID). */
export const MODEL_CATALOGUE = Object.fromEntries(
  Object.entries(BEDROCK_OFFERINGS).map(([key, offering]) => [key, catalogueModel(offering)]),
) as { readonly [K in OfferingKey]: CatalogueModel };

/** `MODEL_ROLES` order (a runtime import would be circular); a test pins it. */
export const VIEW_ROLES = [
  "chat",
  "reasoning",
  "fast",
  "vision",
] as const satisfies readonly ModelRole[];

/**
 * Default role → candidates per geography, read by the Bedrock adapter and by infra (which
 * checks them against each stage's granted geographies): derived from the default scope's
 * families (`deriveCandidates`, model-picking D4), each offering's profile in that geography.
 * Each ID's prefix is its geography: the EU fallbacks of a `global` residency come from
 * resolving over `["global", "eu"]`, never from EU IDs listed under `global`.
 */
export const DEFAULT_BEDROCK_MODEL_IDS: Readonly<
  Record<InferenceGeography, Readonly<Record<ModelRole, readonly string[]>>>
> = Object.fromEntries(
  VIEW_GEOGRAPHIES.map((geography) => [
    geography,
    Object.fromEntries(
      VIEW_ROLES.map((role) => [
        role,
        deriveCandidates(role, defaultDerivationScope(modelAcceptsImages), geography).map((key) =>
          profileOf(key, geography),
        ),
      ]),
    ),
  ]),
) as Record<InferenceGeography, Record<ModelRole, string[]>>;

function profileOf(key: OfferingKey, geography: InferenceGeography): string {
  const profile = (BEDROCK_OFFERINGS[key] as BedrockOffering).routes.profiles[geography];
  if (!profile) {
    throw new Error(`${key} has no ${geography} profile`);
  }
  return profile;
}

/**
 * A foundation model ID without its lab prefix: `anthropic.claude-sonnet-5-5` → `claude-sonnet-5-5`,
 * `meta.llama-x` → `llama-x`; an ID without a prefix is returned as is. The base-name key of
 * `priceKeysOf` (`../models.ts`), shared here so the price table and the lookup never disagree.
 */
export function baseModelNameOf(foundationId: string): string {
  return foundationId.slice(foundationId.indexOf(".") + 1);
}

/**
 * List prices (USD per million tokens) keyed by route model ID (`eu.anthropic.claude-sonnet-5-5`,
 * `anthropic.claude-sonnet-5-5`) and by base model name (`baseModelNameOf`: `claude-sonnet-5-5`,
 * at the foundation price), the keys `priceKeysOf` looks up.
 */
export const LIST_PRICES: Readonly<Record<string, OfferingPrice>> = Object.fromEntries(
  Object.values(BEDROCK_OFFERINGS).flatMap((offering: BedrockOffering) => {
    const { foundation, profiles } = offering.routes;
    const prices: Readonly<Partial<Record<PriceRoute, OfferingPrice>>> = offering.price;
    const routes = Object.entries({ foundation, ...profiles }).flatMap(([route, id]) => {
      const price = prices[route as PriceRoute];
      return id && price ? [[id, price] as const] : [];
    });
    const base = prices.foundation;
    return base ? [...routes, [baseModelNameOf(foundation), base] as const] : routes;
  }),
);

/** The canonical model an offering serves (always generated: the drift test checks it). */
export function canonicalModelOf(key: OfferingKey): CanonicalModel | undefined {
  return CANONICAL_MODELS[BEDROCK_OFFERINGS[key].canonical];
}

/** Whether a catalogued model accepts image input (its canonical `modalities.input`). */
export function modelAcceptsImages(
  key: OfferingKey,
  canonical: (key: OfferingKey) => CanonicalModel | undefined = canonicalModelOf,
): boolean {
  return canonical(key)?.modalities.input.includes("image") ?? false;
}
